import { LitElement, html, css, unsafeHTML, paths } from '@hh/global.js'

class HHMetafieldAccordion extends LitElement {
  static properties = {
    title: {
      type: String,
      attribute: 'title',
    },
    mainMetafield: {
      type: String,
      attribute: 'main-metafield',
    },
    fallbackMetafield: {
      type: String,
      attribute: 'fallback-metafield',
    },
    content: {
      type: Object,
      reflect: true,
    }
  }

  constructor() {
    super()
    this.title = ''
    this.mainMetafield = ''
    this.fallbackMetafield = ''
    this.content = null
    this.parent = this.closest(`.${this.dataset.parent}`)
  }

  connectedCallback() {
    super.connectedCallback()
    this.getContent()
  }

  firstUpdated() {
    this.accordion = this.shadowRoot.querySelector('.accordion')
    window.addEventListener('accordionClick', event => {
      if (event.detail.parent === this.dataset.parent && event.detail.accordion !== this.accordion) {
        this.accordion.close()
      }
    })
  }

  getContent() {
    let parsedContent
    try {
      parsedContent = JSON.parse(this.mainMetafield)
    } catch {
      console.log('Fail to parse main metafield')
    }

    if (!parsedContent) {
      try {
        parsedContent = JSON.parse(this.fallbackMetafield)
      } catch {
        console.log('Fail to parse fallback metafield')
      }
    }

    if (!parsedContent) return
    this.renderContent(parsedContent)
  }

  renderContent(parsedContent) {
    let renderContent = ''
    if (!parsedContent.children) return
    parsedContent.children.forEach((content) => {
      if (!content.children) return
      const type = content.type === 'heading' ? `h${content.level}` : 'p'

      let innerContent = ''
      content.children.forEach((child) => {
        if (child.type === 'text') {
          const openTag = child.bold === true && child.italic === true ? '<strong><i>' : child.bold === true ? '<strong>' : child.italic === true ? '<i>' : ''
          const closeTag = child.bold === true && child.italic === true ? '</i></strong>' : child.bold === true ? '</strong>' : child.italic === true ? '</i>' : ''
          innerContent += `${openTag}${child.value}${closeTag}`
        } else if (child.type === 'link') {
          innerContent += `<a href="${child.url}">${child.children[0].value}</a>`
        }
      })
      renderContent += `<${type}>${innerContent}</${type}>`
    })

    this.content = renderContent
  }


  render() {
    return html`
      <hh-accordion class='accordion' .inherit=${this.parent} data-parent="${this.dataset.parent}">
        <button class='accordion-summary'>
          <span class='summary-title'>${this.title}<span slot="title"></span></span>
          <span class='summary-icon'>${unsafeHTML(paths.plus)}</span>
        </button>
        <div class='accordion-content'>
          <div class='rte'>${unsafeHTML(this.content)}</div>
        </div>
      </hh-accordion>
    `
  }

  static styles = css`
  :host {
    display: block;
  }

  .accordion {
    display: block;
    position: relative;
  }

  .accordion-summary {
    display: flex;
    align-items: center;
    width: 100%;
    border: 0;
    padding: var(--hh-spacing-sm) 0;
    background-color: unset;
    cursor: pointer;
    color: var(--hh-color-night);
    border-bottom: 1px solid var(--hh-color-night);
    justify-content: space-between;
  }

  .accordion-summary:focus {
    border-bottom: 1px solid var(--hh-color-night);
  }

  .summary-title {
    font: var(--hh-utility-font);
    letter-spacing: var(--hh-utility-letter-spacing);
    text-transform: var(--hh-utility-text-transform);
    line-height: 110%;
  }

  .summary-icon {
    width: 1rem;
    display: flex;
  }

  .summary-icon svg {
    width: 1rem;
  }

  .accordion-summary.active path {
    transition: transform var(--hh-duration-default) var(--hh-animation-easing);
  }

  .accordion-summary.active path:first-of-type {
    transform: rotate3d(1, 1, 0, 180deg);
  }

  .accordion-summary:focus-visible {
    outline: unset;
    outline-offset: 0;
  }

  .accordion-summary:focus-visible:not(:focus):after {
    content: '';
    position: absolute;
    left: 0;
    width: 100%;
    height: 0.2rem;
    overflow: hidden;
    background-color: var(--hh-color-focus);
    bottom: -0.1rem;
    z-index: 1;
  }

  .accordion-content {
    position: relative;
    top: var(--hh-spacing-xs);
    max-height: 0;
    overflow: hidden;
    transition: max-height var(--hh-duration-default) ease;
    pointer-events: unset;
    color: var(--hh-color-subdued);
    visibility: hidden;
  }

  .accordion-summary.active + .accordion-content {
    pointer-events: all;
    visibility: visible;
  }

  .accordion-content .rte > div {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: var(--hh-spacing-xs);
    margin-bottom: 0;
  }

  .accordion-content .rte * {
    font: var(--hh-body-font);
    margin: 0;
    min-height: 14px;
  }

  .accordion-content .rte > div > * {
    margin: 0;
  }

  .rte a {
    color: var(--hh-color-text);
    text-decoration: underline;
    text-underline-offset: 0.3rem;
    text-decoration-thickness: 0.1rem;
    transition: text-decoration-thickness var(--hh-duration-short) ease;
  }


  :host *:focus-visible {
    outline: 0.2rem solid var(--hh-color-focus);
    outline-offset: var(--hh-focus-outline-offset);
  }

  :host *:focus:not(:focus-visible) {
    outline: 0;
  }
  `
}

customElements.define('hh-metafield-accordion', HHMetafieldAccordion)
